import * as React from "react";
import { QueryKey, useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";

import { notify } from "Components/ui";

interface ApiMutationOptions<TData, TVariables> {
  /** Success toast (static text or built from the result). */
  success?: string | ((data: TData, variables: TVariables) => string);
  /** Query key prefixes to refetch afterwards. Defaults to everything under ["billing"]. */
  invalidate?: QueryKey[];
  /** Toast the API error message on failure (default true). */
  toastErrors?: boolean;
  onSuccess?: (data: TData, variables: TVariables) => void;
}

/** useMutation + success toast + cache invalidation + API error toast. */
export const useApiMutation = <TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: ApiMutationOptions<TData, TVariables> = {}
): UseMutationResult<TData, unknown, TVariables> => {
  const queryClient = useQueryClient();
  const { success, invalidate, toastErrors = true, onSuccess } = options;
  return useMutation<TData, unknown, TVariables>({
    mutationFn,
    onSuccess: (data, variables) => {
      if (success) {
        notify.success(typeof success === "function" ? success(data, variables) : success);
      }
      (invalidate || [["billing"]]).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
      if (onSuccess) {
        onSuccess(data, variables);
      }
    },
    onError: (error) => {
      if (toastErrors) {
        notify.error(error);
      }
    },
  });
};

/** Returns `value` once it has stopped changing for `delayMs`. Pass a primitive (e.g. JSON). */
export const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
};

/** Re-renders every `intervalMs` so relative times ("3 min ago") stay fresh. */
export const useNow = (intervalMs = 60000): number => {
  const [now, setNow] = React.useState<number>(() => Date.now());
  React.useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(handle);
  }, [intervalMs]);
  return now;
};

/**
 * Closes a dialog after react-query's batched observer notifications (scheduled with
 * setTimeout(0) in v4) have flushed, so a dialog that owns queries or mutations never receives a
 * late update after unmounting (React 17 dev warning).
 */
export const closeAfterFlush = (close: () => void): void => {
  window.setTimeout(close, 0);
};
